import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Synchronous access to a scene's resources by id from node components, without
 * the async useResource hook. With no provider it is empty, so a component
 * renders its placeholder instead of throwing.
 */
import { createContext, useContext, useMemo } from 'react';
// `findSubResource` lives in the pure resources layer (next to its ExtResource
// twin, `findExtResource`); re-exported here for its many R3F importers.
export { findSubResource } from '../resources/SubResourceResolver';
/** No resources: the pools of a scene with none, or of a level that has not resolved. */
export const NO_RESOURCES = {
    internalResources: [],
    externalResources: [],
};
const SceneResourcesContext = createContext(NO_RESOURCES);
SceneResourcesContext.displayName = 'SceneResourcesContext';
export function useSceneResources() {
    return useContext(SceneResourcesContext);
}
/**
 * Stable empty defaults. A `= []` default is a new array every render, and as a
 * `useMemo` dependency it re-fires every `useSceneResources()` consumer.
 */
const NO_INTERNAL = [];
const NO_EXTERNAL = [];
export function SceneResourcesProvider({ internalResources = NO_INTERNAL, externalResources = NO_EXTERNAL, children, }) {
    // This scene's own id shadows the parent's. A merged sub-scene (ADR-0013) re-dispatches under
    // this provider, and the children the host added under its instance node carry host ids. An
    // external id resolves to its last holder and an internal id to its first, so the own table
    // goes after the parent's external table and before its internal one.
    const parent = useContext(SceneResourcesContext);
    const value = useMemo(() => ({
        internalResources: [...internalResources, ...parent.internalResources],
        externalResources: [...parent.externalResources, ...externalResources],
    }), [internalResources, externalResources, parent]);
    return _jsx(SceneResourcesContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=SceneResourcesContext.js.map